/**
 * components/hazard/RemedySection.js — «ЗӨРЧЛИЙГ АРИЛГАХ АРГА ХЭМЖЭЭ».
 *
 * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ:
 *   Хуучин зөрчлийн бүртгэлд (`Inspection.Register`) ЭДГЭЭР талбар
 *   байсан ч шинэ маягтад огт байхгүй байв:
 *     `description_comment` — зөрчлийг арилгах арга хэмжээ
 *     `tn` / `shortname`    — хариуцагч
 *     `end_date`            — гүйцэтгэх хугацаа
 *     `time_comment`        — хугацааны тайлбар
 *
 *   Хяналт шалгалт нь ШАЛГАХ ХУУДСААР явдаг тул үзүүлэлт бүр өөрийн
 *   мөртэй. Харин зөрчил нь хуудасгүй — «ямар зөрчил вэ», «хэн юу
 *   хийх вэ», «хэзээ хүртэл» гэдгийг ЭНД бичихээс өөр газар алга.
 *   Эдгээргүйгээр бүртгэсэн зөрчил нь эзэнгүй, хугацаагүй үлдэж,
 *   хэн ч арилгахгүй.
 *
 * ⚠ Хариуцагчийг ТН-ЭЭР сонгоно, гараар бичихгүй. Нэрээр бичвэл нэг
 *   хүн олон хэлбэрээр бүртгэгдэж, «хэнд хэдэн зөрчил ноогдсон»
 *   гэдгийг тоолох боломжгүй болно.
 *
 * ⚠ АРГА ХЭМЖЭЭ нь ЛАВЛАХААС сонгогдоод, дээр нь чөлөөт ТАЙЛБАР бичнэ.
 *   Зөвхөн чөлөөт текст байсан үед «Сургалт хийсэн», «сургалтад
 *   хамруулсан», «зааварчилгаа өгсөн» гэх мэт нэг зүйл олон хэлбэрээр
 *   бүртгэгдэж, «ямар арга хэмжээ хэр үр дүнтэй байна» гэдгийг
 *   тоолох боломжгүй байв.
 *
 * Props:
 *   value    : { remedy, measure_id, measure_text, assigneeTn, assigneeName,
 *                startDate, dueDate, dueNote,
 *                responsibleBnCode, responsibleBnName }
 *   onChange : (partial) => void
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, DatePicker, Input, Radio, Select, Spin } from "antd";
import moment from "moment";

import { Card, SectionTitle } from "./common";
import RefLink from "./RefLink";
import DepartmentTseh from "src/components/departmentTseh";
// ХАРИУЦАГЧИЙН ХЭСЭГ — жагсаалтыг хумихад (2026-10-31)
import DepartmentNegj from "src/components/departmentNegj";
import { xabInspectionApi } from "src/api/xabApi";
import { listViolationRefs } from "src/api/hazard";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

/**
 * ⚠ Backend нь `{ workers: [...], total }` буцаадаг — `items` БИШ.
 *   Өмнө нь `d?.items || d` гэж уншдаг байсан тул массив ОГТ гардаггүй,
 *   Хариуцагчийн жагсаалт ҮРГЭЛЖ ХООСОН байв (`/xab/workers` дуудагдаж
 *   байсан ч үр дүн нь хаягдаж байсан).
 *
 * ⚠ Талбарын нэр ч өөр: `name` / `position` (`shortname` / `position_name`
 *   биш). Хуучин зураглал нь нэрийг `undefined` болгож байсан.
 */
const asWorkers = (d) => {
  const rows = d?.workers || d?.items || (Array.isArray(d) ? d : []);
  return Array.isArray(rows) ? rows : [];
};

const workerLabel = (w) => {
  const nm = w.name || w.shortname || w.fullname || String(w.tn || "");
  const pos = w.position || w.position_name;
  return pos ? `${nm} — ${pos}` : `${nm} (${w.tn})`;
};

/**
 * @param extras — ШИНЭ талбарууд (лавлахын арга хэмжээ · хариуцагч БН ·
 *   эхлэл-төгсгөл хугацаа) гарах эсэх.
 *
 * ⚠ `false` үед ХУУЧИН 4 талбар л үлдэнэ: арга хэмжээ (чөлөөт текст) ·
 *   хариуцагч · гүйцэтгэх хугацаа (нэг огноо) · хугацааны тайлбар.
 *   `/inspection/register` нь энэ компонентыг `/incident/register`-тэй
 *   ХУВААЛЦДАГ бөгөөд өөрийн шалгах хуудсаар ажилладаг тул урьдын
 *   маягтаараа үлдэх ёстой.
 */
export default function RemedySection({
  value = {},
  onChange,
  extras = true,
  /* Хугацааны ТӨРЛИЙН радио (2026-11-01).
     ⚠ АНХДАГЧААР ХААЛТТАЙ: `due_kind` нь
       `inspection_item_result`-д хадгалагддаг тул зөрчлийн
       БҮРТГЭЛ (/incident/register — `hazard_reports.sla_deadline`)
       дээр гаргавал сонголт ЧИМЭЭГҮЙ алдагдана. */
  dueKindEnabled = false,
  /**
   * ЗӨВХӨН нэг группийг зурах: "measure" | "responsible".
   *
   * ⚠ Хэрэглэгч (2026-11-02): «Зөрчлийг арилгах арга хэмжээ-г
   *   шийтгэлийн төрөлийн дор оруулах». Тэр хоёр нь «юу хийх вэ»
   *   гэсэн нэг утгатай тул нэг баганад багцлагдана; Хариуцагч нь
   *   БН · хүн · огноотой тул доороо бүтэн мөр эзэлнэ.
   * ⚠ Өгөөгүй бол ӨМНӨХ зан төлөв (хоёр группээ өөрөө зурна) —
   *   аюул мэдээлэх маягт эвдрэхгүй.
   */
  only = null,
}) {
  /* ── ЛАВЛАХААС: ХУГАЦААНЫ ТӨРӨЛ ба ХАРИУЦАГЧИЙН ТӨРӨЛ ────────────
     Хэрэглэгч (2026-11-01): «гүйцэтгэх хугацааны төрөл болон
     хариуцагчийн төрлүүдийг лавлах санд оруулж тэндээс удирддаг
     болгох» → `xab.violation_reference` (kind='due_kind' /
     'responsible_kind'), ХАБЭА нь /risk/reference-ээс удирдана.

     ⚠⚠ НЭР нь ЛАВЛАХААС, ЗАН ҮЙЛ нь КОДООС. «тогтмол = огноогүй»,
       «яаралтай = өнөөдөр», «бүлэгт хуваарилсан = ТН шаардахгүй»
       гэдгийг сервер `code`-оор шийднэ (`finding_due.py`). Лавлахад
       ШИНЭ мөр нэмбэл тэр нь аюулгүй тал руу буурна — огноо ба
       хариуцагч заавал шаардагдана.
     ⚠ ЛАВЛАХ УНАСАН/ХООСОН бол доорх ЗАСВАРЫН жагсаалтыг барина:
       хоосон радио нь хэрэглэгчийг гацаана, мөн хуучин бүртгэлийн
       төрөл нь нэргүй болж «(тодорхойгүй)» гэж харагдана. */
  const [dueKindRefs, setDueKindRefs] = useState([]);
  const [respKindRefs, setRespKindRefs] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!dueKindEnabled) return undefined;
    listViolationRefs("due_kind")
      .then((d) => alive && setDueKindRefs(d?.items || []))
      .catch(() => alive && setDueKindRefs([]));
    listViolationRefs("responsible_kind")
      .then((d) => alive && setRespKindRefs(d?.items || []))
      .catch(() => alive && setRespKindRefs([]));
    return () => {
      alive = false;
    };
  }, [dueKindEnabled]);

  /* Лавлах хоосон/унасан үед барих ЗАСВАРЫН жагсаалт — кодууд нь
     серверийн `finding_due.py`-тай ЯГ ижил байх ёстой. */
  const DUE_FALLBACK = [
    { code: "standing", name: "Тогтмол" },
    { code: "urgent", name: "Яаралтай" },
    { code: "urgent_standing", name: "Яаралтай, цаашид тогтмол" },
    { code: "dated", name: "Хугацааны сонголттой" },
  ];
  const RESP_FALLBACK = [
    { code: "person", name: "Тодорхой хүн" },
    { code: "first_line", name: "Анхан шатны удирдах ажилтнууд" },
    { code: "ita", name: "Инженер техникийн ажилчид" },
  ];
  const dueKindList = useMemo(() => {
    const rows = (dueKindRefs || []).filter((r) => r.code);
    return rows.length ? rows : DUE_FALLBACK;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueKindRefs]);
  const respKindList = useMemo(() => {
    const rows = (respKindRefs || []).filter((r) => r.code);
    return rows.length ? rows : RESP_FALLBACK;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respKindRefs]);
  /* ХАРИУЦАГЧИЙН ТӨРӨЛ — `person` (эсвэл хоосон) бол ХҮН сонгоно.
     ⚠ Бусад (бүлэгт хуваарилсан) үед хүний сонгогч ГАРАХГҮЙ: тэр үед
       сервер ТН шаардахгүй бөгөөд сонгосон хүн нь утгагүй болно. */
  const respKind = value.responsibleKind || "person";
  const byPerson = respKind === "person";

  // ── ЛАВЛАХЫН АРГА ХЭМЖЭЭ ───────────────────────────────────────────
  const [measures, setMeasures] = useState([]);
  useEffect(() => {
    let alive = true;
    listViolationRefs("measure")
      .then((d) => alive && setMeasures(d?.items || []))
      .catch(() => alive && setMeasures([]));
    return () => {
      alive = false;
    };
  }, []);

  // ── ХАРИУЦАГЧИЙН ЖАГСААЛТ ──────────────────────────────────────────
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  // Хайлтын хариу СОЛИГДОХ дараалал баталгаагүй — сүүлийн хүсэлтийг
  // тэмдэглэж, хоцорч ирсэн хариуг ХАЯНА. Эс бөгөөс хэрэглэгч бичсээр
  // байхад өмнөх үсгийн хариу дээр нь буудаг.
  const seq = useRef(0);

  const bn = value.responsibleBnCode || "";

  /* ── ХАРИУЦАГЧИЙН ХЭСЭГ — ЗӨВХӨН ШҮҮЛТ (2026-10-31) ────────────────
     Хэрэглэгч: «Хариуцагч БН дараа нь хэсэг сонголт байхгүй бол
     ажилтаны нэрс их олон орж ирж байна».

     ⚠ БААЗАД ХАДГАЛАГДАХГҮЙ: `responsible_bn_code` нь ХАРИУЦАХ НЭГЖ
       (тайлан, хариуцлага тооцох нэгж) бөгөөд хэсгийн кодоор дарж
       бичвэл тайлангийн нэгж өөрчлөгдөнө. Тиймээс хэсэг нь зөвхөн
       сонгогчийг хумих ЛОКАЛ шүүлт — `value`-д хүрэхгүй.

     ⚠ Сервер нь `bn_code`/`tseh_code`-ийн ХАМГИЙН УРТЫГ хамрах хүрээ
       болгодог (`worker_lookup._in_scope`) тул хэсгийн кодыг (4-6
       орон) явуулахад цехийн код автоматаар дарагдана. */
  const [respGrp, setRespGrp] = useState("");
  const [respParentId, setRespParentId] = useState(null);
  // Хайлтын хамрах хүрээ — хэсэг сонгосон бол ТЭР (нарийн), эс бөгөөс БН
  const scopeCode = respGrp || bn;

  /* ── БҮЛГИЙН ЖАГСААЛТ — ЧЕКЛЭЖ ОЛОН ХҮН СОНГОХ (2026-11-03) ───────
     Хэрэглэгч: «Анхан шатны удирдах ажилтан гэж сонговол тухайн БН
     эсвэл хэсгийн АШУА ны жагсаалт чек хийх боломжтой орж ирнэ, хэрэв
     инженер тех ажилчид гэх үед … инж тех ажиллагсадын нэрс чек хийх
     хэлбэрээр … тухайн зөрчлийг хэд хэдэн хүнд хариуцуулж болох юм».

     ⚠ АНГИЛЛЫГ ХЭРХЭН ТОДОРХОЙЛОВ (хэмжсэн 2026-11-02):
       · АШУА  = SSO бүлэг 168 (567 хүн, 46 цех)
       · ИТА   = HR rate_id 2 «Мэргэжилтэн /ИТА/» (1 430 хүн, 49 цех)
       `mgmt=1` нь ТОХИРОХГҮЙ — тэр нь rate 1 ба 2-ыг ХАМТАД нь авдаг.
     ⚠ Хамрах хүрээ нь сонгосон БН/ХЭСЭГ (`scopeCode`) — сервер
       хамгийн УРТ кодыг авдаг тул хэсэг сонгосон бол хэсгээрээ. */
  const KIND_FILTER = { first_line: { group: "168" }, ita: { rate: "2" } };
  const [kindPeople, setKindPeople] = useState([]);
  const [kindLoading, setKindLoading] = useState(false);
  const kindSeq = useRef(0);

  useEffect(() => {
    const f = KIND_FILTER[respKind];
    if (!f || !scopeCode) {
      setKindPeople([]);
      return;
    }
    const my = ++kindSeq.current;
    setKindLoading(true);
    xabInspectionApi
      .searchWorkers({ bn_code: scopeCode, ...f, limit: 300 })
      .then((d) => {
        if (my === kindSeq.current) setKindPeople(asWorkers(d));
      })
      .catch(() => {
        if (my === kindSeq.current) setKindPeople([]);
      })
      .finally(() => {
        if (my === kindSeq.current) setKindLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respKind, scopeCode]);

  /* Сонгогдсон ТН-ууд — ҮРГЭЛЖ массив (хуучин мөрд огт байхгүй). */
  const pickedTns = Array.isArray(value.responsibleTns)
    ? value.responsibleTns.map(String)
    : [];


  /**
   * ⚠ `mgmt=1` — ЗӨВХӨН удирдах ажилтан ба мэргэжилтэн (ИТА).
   *   Зөрчил арилгах даалгаврыг ажилчинд өгвөл хэрэгжихгүй: түүнд
   *   нөөц зарцуулах, бусдад үүрэг өгөх эрх байхгүй. Сервер тал
   *   `rate_id ∈ {1,2}`-оор шүүнэ (7 101 → 2 005 хүн).
   */
  const fetchWorkers = useCallback(
    (q) => {
      const text = String(q || "").trim();
      // БН сонгогдсон бол хайлтгүйгээр ч бүтэн жагсаалтыг харуулна —
      // тэр нь дунджаар 35 хүн. БН-гүй үед л бичихийг шаардана.
      if (!scopeCode && text.length < 2) {
        setOptions([]);
        return;
      }
      const my = ++seq.current;
      setSearching(true);
      xabInspectionApi
        .searchWorkers({
          /* ⚠ ХЭСЭГ сонгогдсон бол ТҮҮНИЙ код (нарийн), эс бөгөөс БН.
             Сервер нь хамгийн УРТ кодыг хамрах хүрээ болгоно. */
          ...(scopeCode ? { bn_code: scopeCode } : {}),
          ...(text ? { q: text } : {}),
          // ⚠ `mgmt` нь ЗӨРЧЛИЙН маягтын шинэ дүрэм. Хяналт шалгалтын
          //   хуудсанд (`extras=false`) хуучнаар БҮХ ажилтнаас хайна.
          ...(extras ? { mgmt: 1 } : {}),
          limit: extras ? 200 : 20,
        })
        .then((d) => {
          if (my !== seq.current) return;
          setOptions(asWorkers(d));
        })
        .catch(() => {
          if (my === seq.current) setOptions([]);
        })
        .finally(() => {
          if (my === seq.current) setSearching(false);
        });
    },
    [scopeCode, extras],
  );

  // БН солигдох бүрд жагсаалтыг ДАХИН татна.
  useEffect(() => {
    fetchWorkers("");
  }, [fetchWorkers]);

  // Засварлах үед сонгогдсон хүн жагсаалтад байхгүй бол харагдахгүй
  // болно — түүнийг сонголтод нэмж өгнө.
  useEffect(() => {
    if (!value.assigneeTn || !value.assigneeName) return;
    setOptions((prev) =>
      prev.some((w) => String(w.tn) === String(value.assigneeTn))
        ? prev
        : [{ tn: value.assigneeTn, name: value.assigneeName }, ...prev],
    );
  }, [value.assigneeTn, value.assigneeName]);

  const label = (t, req) => (
    <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 2 }}>
      {t}
      {req ? <b style={{ color: "#DC2626" }}> *</b> : null}
    </div>
  );

  // Маягтад аль хэдийн ӨНГӨРСӨН огноо байгаа эсэх = хуучин бүртгэл засаж
  // байна гэсэн үг (шинэ бүртгэлд ийм утга орох замгүй).
  const hasPastDate =
    (value.startDate && moment(value.startDate).isBefore(moment(), "day")) ||
    (value.dueDate && moment(value.dueDate).isBefore(moment(), "day"));

  /* ── ХОЁР ХЭСЭГ, НЭГ МӨРӨНД (2026-11-01) ──────────────────────────
     Хэрэглэгч: «Зөрчлийг арилгах арга хэмжээ-г салгаж Хариуцагч гэсэн
     2 хэсэг болгож 1 мөрөнд 2 багана байдлаар харуулах».

     ⚠ ЗӨВХӨН `extras` (зөрчлийн маягт) үед хуваана. Аюул мэдээлэх
       маягт (`extras=false`) нь ердөө тайлбар + хариуцагч + огноотой
       тул хоёр хоосон карт болж харагдана.
     ⚠ 900px-ээс доош НЭГ багана — composer-ийн `hfc-grid`-тай ИЖИЛ хил. */
  const twoCol = !!extras;
  const styleTag = (
    <style>{`
      .rs-2col { display: grid; grid-template-columns: 1fr 1fr;
                 gap: 12px; align-items: start; }
      /* ── ХАРИУЦАГЧ ГРУППИЙН ДОТОР 2 БАГАНА (2026-11-03, хэрэглэгч:
           «Зөрчил бүртгэх дээр Хариуцагч хэсэгийг 2 баган болгох
            Хариуцагчийн төрөл / Хариуцагч / Гүйцэтгэх хугацаа энэ 3 г
            нэг групп болгож» + «Хугацааны тайлбар бас»).
         ⚠ Энэ нь rs-2col-ООС ӨӨР ТҮВШИН: тэр нь ГРУППҮҮДИЙГ
           (Арга хэмжээ | Хариуцагч) зэрэгцүүлдэг, энэ нь НЭГ группийн
           ДОТОРХ талбаруудыг.
         ⚠ 900px-ээс доош НЭГ багана — rs-2col ба composer-ийн
           hfc-grid-тэй ИЖИЛ хил. Гурав дахь хэмнэл нэмбэл утсан
           дээр хэсгүүд өөр өөр цэгт эвхэгдэнэ.
         ⚠ ТАЙЛБАРТ BACKTICK БИЧИХГҮЙ: style нь template literal
           тул мөр тасарч build БҮХЭЛДЭЭ унана. */
      .rs-resp2 { display: grid; grid-template-columns: 1fr 1fr;
                  gap: 10px 12px; align-items: start; }
      .rs-resp2 > * { min-width: 0; }
      /* ── ГҮЙЦЭТГЭХ ХУГАЦАА — 2 БАГАНА (2026-11-03, хэрэглэгч:
           «Гүйцэтгэх хугацаа сонголтын хэсгийг 2 багана болгож
            харуулах эхлэл төгсгөл хэсгийг баруун хэсэгт»).
         ЗҮҮН = хугацааны ТӨРӨЛ (радио), БАРУУН = эхлэл/төгсгөл.
         ⚠ auto-fit: огнооны муж нь ЗӨВХӨН «Хугацааны сонголттой»
           үед гардаг тул нуугдахад радио нь бүтэн өргөнөө эзэлнэ
           (хоосон багана үлдэхгүй). */
      /* ⚠⚠ 2026-11-05: ХЭРЭГЛЭГЧ «Гүйцэтгэх хугацаа хэсгийг доош
           цуваа харуул» гэснээр НЭГ БАГАНА болов — 2026-11-03-ны
           «2 багана, эхлэл/төгсгөл баруун талд» гэсэн шийдвэрийг
           ХЭРЭГЛЭГЧ ӨӨРӨӨ хүчингүй болгосон. Дахин 2 багана болговол
           тэр хүсэлтийг эсэргүүлнэ.
         ⚠ Хугацааны ТӨРӨЛ (радио) ба эхлэл/төгсгөл хоёр ДАРААЛЛААР
           доошоо. Радио тус бүр ч өөрийн мөрөнд (доорх
           .rs-duelist дүрэм). */
      .rs-due2 { display: grid; grid-template-columns: 1fr;
                 gap: 8px; align-items: start; }
      .rs-due2 > * { min-width: 0; }
      /* Хугацааны төрлийн радио — ТУС БҮР ӨӨРИЙН МӨРӨНД («цуваа»).
         ⚠ antd нь радиог inline тавьдаг тул display:flex хэрэгтэй;
           margin-inline-end-ийг ч тэглэнэ, эс бөгөөс баруун талд
           хоосон зай үлдэнэ. */
      .rs-duelist .ant-radio-wrapper { display: flex; margin-inline-end: 0;
                                       margin-bottom: 2px; }
      @media (max-width: 900px) {
        .rs-resp2 { grid-template-columns: 1fr; gap: 10px; }
      }
      .rs-2col > div { min-width: 0; }
      .rs-2col .hz-card { margin-bottom: 0 !important; }
      @media (max-width: 900px) {
        .rs-2col { grid-template-columns: 1fr; gap: 10px; }
      }
    `}</style>
  );

  const groupMeasure = (
    <>
        {extras ? (
        <div>
          {label("Арга хэмжээ")}
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder={
              measures.length ? "Лавлахаас сонгоно уу" : "Лавлахад бүртгэгдээгүй"
            }
            disabled={!measures.length}
            optionFilterProp="label"
            value={value.measure_id ?? undefined}
            onChange={(id) => {
              const m = measures.find((r) => r.id === id);
              onChange({
                measure_id: id ?? null,
                measure_text: m ? (m.code ? `${m.code} · ${m.name}` : m.name) : null,
              });
            }}
            options={measures.map((m) => ({
              value: m.id,
              label: m.code ? `${m.code} · ${m.name}` : m.name,
            }))}
          />
          {!value.measure_id && value.measure_text ? (
            <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
              Бүртгэгдсэн: {value.measure_text}
            </div>
          ) : null}
        </div>
        ) : null}

        <div style={extras ? { marginTop: 10 } : undefined}>
          {extras ? label("Тайлбар") : null}
          <TextArea
            rows={3}
            value={value.remedy || ""}
            onChange={(e) => onChange({ remedy: e.target.value })}
            placeholder={
              extras
                ? "Сонгосон арга хэмжээг тодруулж бичнэ үү — юуг, хаана, хэрхэн"
                : "Юу хийж зөрчлийг арилгах вэ — тодорхой бичнэ үү"
            }
          />
        </div>
    </>
  );

  const groupResponsible = (
    <>
        {/* ── ХАРИУЦАГЧ БН ────────────────────────────────────────────
            ⚠ Энэ нь БҮРТГЭСЭН хүний нэгжээс (`insert_dept_*`) ӨӨР.
              Зөрчил гарсан/хариуцах нэгжийг заана — тайлан, хариуцлага
              тооцох нь ЭНЭ нэгжээр явна. Хоёрыг нэгтгэвэл шалгагчийн
              нэгжид бусдын зөрчил овоорно.
            ⚠ Хариуцагчийн ЖАГСААЛТЫГ энэ нэгж тодорхойлно тул Хариуцагч
              сонголтоос ӨМНӨ байрлана. */}
        {extras ? (
        <div style={{ marginTop: 10 }}>
          {label("Хариуцагч БН")}
          <DepartmentTseh
            value={value.responsibleBnCode || undefined}
            onChange={(code, parentId, name) => {
              /* ⚠ `parentId` нь ХЭСГИЙН сонгогчид ЗААВАЛ (`paranetid`) —
                 үүнгүйгээр хэсгийн жагсаалт хоосон гардаг
                 (departmentNegj-ийн толгойн тайлбар). Өмнө нь `_parentId`
                 гэж ҮЛ ХЭРЭГСЭЖ байсан. */
              setRespParentId(parentId || null);
              if (String(code || "") !== String(value.responsibleBnCode || "")) {
                setRespGrp("");           // БН солигдвол хэсгийн шүүлт ч арилна
              }
              onChange({
                responsibleBnCode: code || "",
                responsibleBnName: name || "",
                // ⚠ Нэгж солигдоход хуучин хариуцагчийг ЦЭВЭРЛЭНЭ. Эс
                //   бөгөөс өөр нэгжийн хүн хариуцагчаар үлдэж, шинэ
                //   жагсаалтад ч байхгүй тул анзаарагдахгүй.
                ...(String(code || "") !== String(value.responsibleBnCode || "")
                  ? { assigneeTn: "", assigneeName: "" }
                  : {}),
              });
            }}
          />
        </div>
        ) : null}

        {/* ── ХАРИУЦАГЧИЙН ХЭСЭГ — ЗӨВХӨН ЖАГСААЛТЫГ ХУМИХ ─────────────
            Хэрэглэгч (2026-10-31): «Хариуцагч БН дараа нь хэсэг сонголт
            байхгүй бол ажилтаны нэрс их олон орж ирж байна».

            ⚠ ЗААВАЛ БИШ: хэсэг сонгохыг ШААРДАХГҮЙ. Зөрчил арилгах
              хариуцагч нь тухайн хэсгийн хүн байх албагүй (2026-09-25:
              цахилгааны зөрчлийг Цахилгаан цех арилгана) тул хэсгээр
              ХАТУУ хумивал хариуцагчийг ОГТ сонгож чадахгүй болно.
            ⚠ БААЗАД ХҮРЭХГҮЙ: `responsible_bn_code` нь ХАРИУЦАХ НЭГЖ —
              тайлан, хариуцлага тооцох нэгж. Хэсгийн кодоор дарж бичвэл
              тайлангийн нэгж чимээгүй өөрчлөгдөнө.
            ⚠ БН сонгогдоогүй үед ХАРУУЛАХГҮЙ: эцэггүй хэсгийн жагсаалт
              нь бүх цехийн хэсгийг гаргах эрсдэлтэй. */}
        {extras && bn ? (
          <div style={{ marginTop: 10 }}>
            {label("Хариуцагч хэсэг")}
            <DepartmentNegj
              paranetid={respParentId}
              parentCode={bn}
              /* ⚠ `false` ИЛТ: өөрийн хэсгийг автоматаар сонгуулбал
                 хэрэглэгчийн мэдэлгүй жагсаалт хумигдаж, «хариуцагч
                 олдсонгүй» гэж харагдана. */
              autoSelectOwn={false}
              value={respGrp || undefined}
              onChange={(code) => {
                setRespGrp(code || "");
                // Хэсэг солигдвол хуучин хариуцагч шинэ жагсаалтад
                // байхгүй байж болно — БН-ийн дүрэмтэй ижил цэвэрлэнэ.
                onChange({ assigneeTn: "", assigneeName: "" });
              }}
            />
            <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
              Заавал биш — хэсэг сонговол хариуцагчийн жагсаалт тэр хэсгийн
              удирдах ажилтнаар хумигдана.
            </div>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {/* ── ХАРИУЦАГЧИЙН ТӨРӨЛ (2026-11-01) ──────────────────────
              Хэрэглэгч: «хариуцагч дээр хүн сонгохоос гадна, анхан
              шатны удирдах ажилтнуудад эсвэл инженер техникийн
              ажилчдад гэсэн сонголт нэмэх».
              ⚠ БҮЛЭГТ хуваарилсан үед ХҮНИЙ сонгогч ГАРАХГҮЙ: сервер
                ТН шаардахгүй бөгөөд сонгосон хүн утгагүй болно.
                Төрөл солиход хуучин ТН-ийг ЦЭВЭРЛЭНЭ — эс бөгөөс
                бүлэгт хуваарилсан зөрчилд нэг хүний нэр чимээгүй
                үлдэнэ.
              ⚠ ХАРИУЦЛАГА СУЛРАХЫГ МЭДСЭЭР: бүлэгт хуваарилсан
                зөрчилд тодорхой эзэн, мэдэгдэл хүрэх хүн алга —
                хэрэглэгчийн ИЛЭРХИЙ шийдвэр. */}
          {dueKindEnabled ? (
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              {label("Хариуцагчийн төрөл")}
              <Select
                style={{ width: "100%" }}
                value={respKind}
                onChange={(v) =>
                  onChange({
                    responsibleKind: v,
                    ...(v === "person"
                      ? {}
                      : { assigneeTn: "", assigneeName: "" }),
                  })
                }
                options={respKindList.map((k) => ({
                  value: k.code,
                  label: k.name,
                  title: k.description,
                }))}
              />

              {/* ── ЧЕКЛЭХ ЖАГСААЛТ (2026-11-03) ──────────────────────
                  Бүлгийн төрөл сонгоход тухайн БН/хэсгийн ажилтнууд
                  гарч, ОЛНЫГ чеклэж болно. Өмнө нь энд юу ч гардаггүй
                  байсан тул «бүлэгт хуваарилсан» зөрчилд тодорхой эзэн
                  үлддэггүй байв.
                  ⚠ Чеклэсэн хүн БҮРД ТУСДАА ДААЛГАВАР үүснэ
                    (`inspection_item_assignment`) — хэрэглэгчийн
                    шийдвэр 2026-11-02: «тус бүрд даалгавар». */}
              {!byPerson ? (
                <div style={{ marginTop: 8 }}>
                  {!scopeCode ? (
                    <div style={{ fontSize: 11.5, color: "#A8AFBC" }}>
                      Эхлээд хариуцагч БН-ээ сонгоно уу.
                    </div>
                  ) : kindLoading ? (
                    <div style={{ fontSize: 11.5, color: "#A8AFBC" }}>
                      <Spin size="small" /> Жагсаалт уншиж байна...
                    </div>
                  ) : !kindPeople.length ? (
                    <div style={{ fontSize: 11.5, color: "#B45309" }}>
                      Энэ нэгжид бүртгэгдсэн хүн олдсонгүй.
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#5B6472",
                          marginBottom: 4,
                        }}
                      >
                        Хариуцах хүмүүсээ сонгоно уу ({pickedTns.length}/
                        {kindPeople.length})
                      </div>
                      {/* ── ЗҮҮН: сонгох жагсаалт · БАРУУН: сонгогдсон
                          (2026-11-03, хэрэглэгч: «чек хийгдсэнг баруун
                           талд харуулах сонгогдсон гэж»).
                          ⚠ Сонгогдсоныг тусад нь харуулах шалтгаан:
                            жагсаалт 90+ хүнтэй байж болох тул (ИТА цех
                            25 → 91) гүйлгэхэд хэнийг сонгосноо алдана. */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                          gap: 8,
                          alignItems: "start",
                        }}
                      >
                      <div
                        style={{
                          maxHeight: 190,
                          overflowY: "auto",
                          border: "1px solid #E5E7EB",
                          borderRadius: 6,
                          padding: "6px 8px",
                        }}
                      >
                        <Checkbox.Group
                          style={{ width: "100%" }}
                          value={pickedTns}
                          onChange={(v) =>
                            onChange({ responsibleTns: (v || []).map(String) })
                          }
                        >
                          {kindPeople.map((w) => (
                            <div key={w.tn} style={{ padding: "1px 0" }}>
                              <Checkbox value={String(w.tn)}>
                                <span style={{ fontSize: 12 }}>
                                  {w.name}
                                  {w.position ? (
                                    <span
                                      style={{
                                        color: "#8A94A6",
                                        marginLeft: 4,
                                      }}
                                    >
                                      · {w.position}
                                    </span>
                                  ) : null}
                                </span>
                              </Checkbox>
                            </div>
                          ))}
                        </Checkbox.Group>
                      </div>

                      {/* БАРУУН — СОНГОГДСОН */}
                      <div
                        style={{
                          maxHeight: 190,
                          overflowY: "auto",
                          border: "1px solid #BFDBFE",
                          background: "#F8FBFF",
                          borderRadius: 6,
                          padding: "6px 8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#1D4ED8",
                            marginBottom: 4,
                          }}
                        >
                          Сонгогдсон ({pickedTns.length})
                        </div>
                        {!pickedTns.length ? (
                          <div style={{ fontSize: 11.5, color: "#A8AFBC" }}>
                            Хараахан сонгоогүй байна.
                          </div>
                        ) : (
                          pickedTns.map((tn) => {
                            /* ⚠ Нэрийг ачаалсан жагсаалтаас олно; олдохгүй
                               бол ТН-ийг харуулна (хоосон мөр үлдээхгүй) —
                               сонгосны дараа нэгж солигдвол ийм болно. */
                            const w = kindPeople.find(
                              (x) => String(x.tn) === tn,
                            );
                            return (
                              <div
                                key={tn}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 6,
                                  padding: "1px 0",
                                }}
                              >
                                <span style={{ fontSize: 12, minWidth: 0 }}>
                                  {w ? w.name : tn}
                                </span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  title="Хасах"
                                  onClick={() =>
                                    onChange({
                                      responsibleTns: pickedTns.filter(
                                        (x) => x !== tn,
                                      ),
                                    })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      onChange({
                                        responsibleTns: pickedTns.filter(
                                          (x) => x !== tn,
                                        ),
                                      });
                                    }
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    color: "#DC2626",
                                    fontSize: 12,
                                    lineHeight: 1,
                                    padding: "0 2px",
                                  }}
                                >
                                  ×
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {byPerson ? (
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
            {label("Хариуцагч")}
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder={
                !extras
                  ? "Нэр эсвэл Б/Д-аар хайх…"
                  : bn
                    ? "Нэгжийн удирдах ажилтнаас сонгох"
                    : "Эхлээд Хариуцагч БН-ээ сонгоно уу"
              }
              filterOption={false}
              onSearch={fetchWorkers}
              notFoundContent={
                searching ? (
                  <Spin size="small" />
                ) : !extras ? (
                  "Хоёроос дээш үсэг бичнэ үү"
                ) : bn ? (
                  "Энэ нэгжид удирдах ажилтан олдсонгүй"
                ) : (
                  "БН сонгох эсвэл хоёроос дээш үсэг бичнэ үү"
                )
              }
              value={value.assigneeTn || undefined}
              onChange={(tn) => {
                const w = options.find((x) => String(x.tn) === String(tn));
                onChange({
                  assigneeTn: tn || "",
                  assigneeName: w ? w.name || w.shortname || "" : "",
                });
              }}
              options={options.map((w) => ({
                value: String(w.tn),
                label: workerLabel(w),
              }))}
            />
          </div>
          ) : null}

          {/* ── ГҮЙЦЭТГЭХ ХУГАЦАА — ЭХЛЭЛ ба ТӨГСГӨЛ ────────────────
              ⚠ Зөвхөн эцсийн огноо байсан үед «хугацаа хэтэрсэн» гэдгээс
                өөр юу ч хэлэх боломжгүй байв. Эхлэл нь ажил хэзээ
                эхлэхийг заадаг тул «эхлээгүй байна» гэдгийг хугацаа
                дуусахаас ӨМНӨ илрүүлнэ. */}
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            {label("Гүйцэтгэх хугацаа")}
            <div className="rs-due2">
            {/* ── ХУГАЦААНЫ ТӨРӨЛ — РАДИО ЛИСТ (2026-11-01) ───────────
                Хэрэглэгч: «гүйцэтгэх хугацааны хэсгийг хэд хэдэн
                сонголттой … 1. тогтмол, 2. яаралтай, 3. яаралтай цаашид
                тогтмол, 4. хугацааны сонголттой».

                ⚠ ОГНОО СОНГОХ нь ЗӨВХӨН «Хугацааны сонголттой» үед.
                  Бусад үед сервер өөрөө тавина (яаралтай = өнөөдөр) тул
                  хоёр газраас зөрүүтэй огноо орохгүй.
                ⚠ «ТОГТМОЛ» үед огноо БАЙХГҮЙ. `due_date` нь заавал
                  бөглөх 4 талбарын нэг боловч сервер «тогтмол»-ыг
                  чөлөөлдөг (`finding_due.needs_due_date`) — эс бөгөөс
                  зөрчил ноорогт гацна. */}
            {extras && dueKindEnabled ? (
              <div style={{ marginBottom: 8 }}>
                <Radio.Group
                  size="small"
                  /* «доош цуваа» — радио тус бүр өөрийн мөрөнд
                     (2026-11-05, дээрх `.rs-duelist` дүрэм). */
                  className="rs-duelist"
                  value={value.dueKind || "dated"}
                  onChange={(e) => {
                    const k = e.target.value;
                    /* ⚠ ОГНООГ ЭНД ТАВИХГҮЙ (серверийн ажил) — зөвхөн
                       ХЭРЭГГҮЙ болсныг цэвэрлэнэ. Клиент огноо тавибал
                       мобайл/офлайн дуудлагатай зөрүүтэй болно. */
                    if (k === "standing") {
                      onChange({ dueKind: k, startDate: null, dueDate: null });
                    } else if (k === "dated") {
                      onChange({ dueKind: k });
                    } else {
                      // яаралтай · яаралтай+тогтмол — огноог сервер
                      // бүртгэсэн өдрөөр тавина
                      onChange({ dueKind: k, dueDate: null });
                    }
                  }}
                >
                  {/* ⚠ ЛАВЛАХААС (2026-11-01) — ХАБЭА нь нэр, эрэмбийг
                      /risk/reference-ээс удирдана. Зан үйл нь КОДООР
                      (`finding_due.py`) тул нэр чөлөөтэй засагдана. */}
                  {dueKindList.map((k) => (
                    <Radio key={k.code} value={k.code} title={k.description}>
                      {k.name}
                    </Radio>
                  ))}
                </Radio.Group>
                {value.dueKind && value.dueKind !== "dated" ? (
                  <div
                    style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}
                  >
                    {value.dueKind === "standing"
                      ? "Байнга биелүүлэх шаардлага — дуусах хугацаа байхгүй."
                      : "Хугацаа нь бүртгэсэн өдрөөр тавигдана." +
                        (value.dueKind === "urgent_standing"
                          ? " Цаашид байнгын шаардлага болно."
                          : "")}
                  </div>
                ) : null}
              </div>
            ) : null}
            {extras && dueKindEnabled && (value.dueKind || "dated") !== "dated"
              ? null
              : (
              <div>
              {extras ? (
            <RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder={["Эхлэл", "Төгсгөл"]}
              value={[
                value.startDate ? moment(value.startDate) : null,
                value.dueDate ? moment(value.dueDate) : null,
              ]}
              // ⚠ ШИНЭ бүртгэлд өнгөрсөн огноо сонгуулахгүй. Хугацаа нь
              //   өнгөрсөн зөрчил бүртгэсэн даруйдаа «хэтэрсэн» болж,
              //   жинхэнэ хоцрогдсон зөрчлүүдийн дунд алдагдана.
              //
              // ⚠ ГЭВЧ ХУУЧИН бүртгэлд энэ хориг ХОР болно: 71300-аас
              //   шилжүүлсэн 2024-2025 оны зөрчлийг засварлахад аль хэдийн
              //   бичигдсэн огноо нь сонгох боломжгүй болж, хэрэглэгч
              //   хугацааг УСТГАХААС өөр аргагүй болно. Тиймээс маягтад
              //   өнгөрсөн огноо аль хэдийн байвал хоригийг цуцална.
              disabledDate={(d) => !hasPastDate && d && d < moment().startOf("day")}
              onChange={(r) =>
                onChange({
                  startDate: r?.[0] || null,
                  dueDate: r?.[1] || null,
                })
              }
            />
            ) : (
              /* Хуучин хэлбэр — ганц эцсийн огноо */
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                value={value.dueDate ? moment(value.dueDate) : null}
                disabledDate={(d) =>
                  !hasPastDate && d && d < moment().startOf("day")
                }
                onChange={(d) => onChange({ dueDate: d || null })}
              />
            )}
              </div>
            )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          {label("Хугацааны тайлбар")}
          <Input
            value={value.dueNote || ""}
            onChange={(e) => onChange({ dueNote: e.target.value })}
            placeholder="Жишээ: сэлбэг ирсний дараа, ээлжит засварын үед"
          />
        </div>
    </>
  );

  if (!twoCol) {
    /* ⚠ ХУУЧИН ХЭЛБЭР ХЭВЭЭР: аюул мэдээлэх маягт нэг картаар. */
    return (
      <Card>
        <SectionTitle right={<RefLink />}>
          Зөрчлийг арилгах арга хэмжээ
        </SectionTitle>
        {groupMeasure}
        {groupResponsible}
      </Card>
    );
  }

  if (only === "measure") {
    return (
      <Card>
        <SectionTitle right={<RefLink />}>
          Зөрчлийг арилгах арга хэмжээ
        </SectionTitle>
        {groupMeasure}
      </Card>
    );
  }
  if (only === "responsible") {
    return (
      <>
        {styleTag}
        <Card>
          <SectionTitle>Хариуцагч</SectionTitle>
          {/* ⚠ `groupResponsible` нь ФРАГМЕНТ тул түүний хүүхдүүд
              ЭНЭ торны ШУУД элемент болно — нэмэлт савлагаа
              шаардлагагүй. */}
          <div className="rs-resp2">{groupResponsible}</div>
        </Card>
      </>
    );
  }

  return (
    <>
      {styleTag}
      <div className="rs-2col">
        <div>
          <Card>
            <SectionTitle right={<RefLink />}>
              Зөрчлийг арилгах арга хэмжээ
            </SectionTitle>
            {groupMeasure}
          </Card>
        </div>
        <div>
          <Card>
            <SectionTitle>Хариуцагч</SectionTitle>
            <div className="rs-resp2">{groupResponsible}</div>
          </Card>
        </div>
      </div>
    </>
  );
}
